export interface PlanFeaturesPayload {
  restaurant?: string[];
  customer?: string[];
  restaurantVisible?: string[];
  customerVisible?: string[];
  discoverable?: boolean;
  platformStatus?: string;
  subscriptionStatus?: string;
  planId?: number;
}

export const CUSTOMER_FEATURE_DINE_IN = 'dine_in';
export const CUSTOMER_FEATURE_TAKEAWAY = 'takeaway';
export const CUSTOMER_FEATURE_DIRECT_DELIVERY = 'direct_delivery';
export const CUSTOMER_FEATURE_TABLE_BOOKING = 'table_booking';
export const CUSTOMER_FEATURE_VIRTUAL_CAPTAIN = 'virtual_captain';
export const CUSTOMER_FEATURE_QUICK_ASSISTANCE = 'quick_assistance';

export type GuestDeliveryConfig = {
  enabled?: boolean;
  featureEnabled?: boolean;
  paused?: boolean;
};

/** Plan has Direct Delivery and the restaurant is accepting new delivery orders. */
export function isGuestDeliveryOpen(
  features: PlanFeaturesPayload | undefined,
  config?: GuestDeliveryConfig | null
): boolean {
  if (!hasCustomerFeature(features, CUSTOMER_FEATURE_DIRECT_DELIVERY) || !config) {
    return false;
  }
  return !!config.enabled && !!config.featureEnabled && !config.paused;
}

/** Guest order type: 1 = dine-in, 2 = delivery, 3 = takeaway. */
export function defaultGuestOrderType(
  features: PlanFeaturesPayload | undefined,
  deliveryConfig?: GuestDeliveryConfig | null
): number {
  if (hasCustomerFeature(features, CUSTOMER_FEATURE_DINE_IN)) {
    return 1;
  }
  if (hasCustomerFeature(features, CUSTOMER_FEATURE_TAKEAWAY)) {
    return 3;
  }
  if (isGuestDeliveryOpen(features, deliveryConfig)) {
    return 2;
  }
  return 3;
}

export function isGuestOrderTypeEnabled(
  features: PlanFeaturesPayload | undefined,
  orderType: number
): boolean {
  if (orderType === 1) {
    return hasCustomerFeature(features, CUSTOMER_FEATURE_DINE_IN);
  }
  if (orderType === 3) {
    return hasCustomerFeature(features, CUSTOMER_FEATURE_TAKEAWAY);
  }
  if (orderType === 2) {
    return hasCustomerFeature(features, CUSTOMER_FEATURE_DIRECT_DELIVERY);
  }
  return true;
}

export function guestCanOrder(
  features: PlanFeaturesPayload | undefined,
  deliveryConfig?: GuestDeliveryConfig | null
): boolean {
  return hasCustomerFeature(features, CUSTOMER_FEATURE_DINE_IN)
    || hasCustomerFeature(features, CUSTOMER_FEATURE_TAKEAWAY)
    || isGuestDeliveryOpen(features, deliveryConfig);
}

export function hasCustomerFeature(
  features: PlanFeaturesPayload | undefined,
  featureKey: string
): boolean {
  if (!features?.customer) {
    return false;
  }
  return features.customer.includes(featureKey);
}

export function hasRestaurantFeature(
  features: PlanFeaturesPayload | undefined,
  featureKey: string
): boolean {
  if (!features?.restaurant) {
    return false;
  }
  return features.restaurant.includes(featureKey);
}

/** Features shown in UI for trial / subscribed restaurants (access may still require upgrade). */
export function hasVisibleCustomerFeature(
  features: PlanFeaturesPayload | undefined,
  featureKey: string
): boolean {
  if (features?.customerVisible?.length) {
    return features.customerVisible.includes(featureKey);
  }
  return hasCustomerFeature(features, featureKey);
}

export function isFeatureDiscoverableTenant(features: PlanFeaturesPayload | undefined): boolean {
  return features?.discoverable === true
    || features?.platformStatus === 'trial'
    || features?.platformStatus === 'active'
    || features?.platformStatus === 'grace';
}
